import { ArrowUpRight, ShoppingBag } from "@phosphor-icons/react";
import { fmt, Row } from "@/pages/checkout/checkoutHelpers";

/**
 * Order summary sidebar for the merch checkout page. Sticky on desktop.
 * Handles two rendering modes:
 *   - `isCartMode` — iterates `cart.items` and shows a scrollable line-item list
 *   - single-product mode — shows the current `product` + `variant` + `activeMockup`
 * Kept as a pure presentational component: no fetches, no local state; the
 * parent MerchCheckout page owns everything and passes it in via props.
 */
export default function OrderSummary({
  isCartMode,
  cart,
  product,
  variant,
  activeMockup,
  quantity,
  itemTotal,
  shipping,
  grandTotal,
  selectedRate,
  submitting,
  shippingComplete,
  recipient,
  onCheckout,
  provider = "stripe",
}) {
  const providerLabel = provider === "square" ? "Square" : "Stripe";
  return (
    <aside data-testid="order-summary" className="space-y-4 md:sticky md:top-6 self-start">
      <div className="border-2 border-[#B32027] bg-[#0F0F0F] p-5 space-y-4">
        <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#B32027]">/ Order summary</p>

        {isCartMode ? (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {cart.items.map((it) => (
              <div key={it.sync_variant_id} className="flex gap-3 border-b border-white/10 pb-2 last:border-0" data-testid={`sum-cart-item-${it.sync_variant_id}`}>
                <div className="w-14 h-14 bg-white flex-shrink-0 overflow-hidden">
                  {it.image && <img src={it.image} alt="" className="w-full h-full object-contain" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold line-clamp-2 leading-snug">{it.product_name}</p>
                  {it.variant_name && <p className="text-white/50 text-[10px]">{it.variant_name}</p>}
                  <p className="text-white/70 text-[11px] mt-0.5">Qty {it.quantity} · ${(it.item_retail_price * it.quantity).toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
        ) : product && (
          <div className="flex gap-3">
            <div className="w-20 h-20 bg-white flex-shrink-0 overflow-hidden">
              {activeMockup ? (
                <img src={activeMockup} alt="" className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/30"><ShoppingBag size={28} weight="thin" /></div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm line-clamp-2 leading-snug">{product.name}</p>
              {variant && <p className="text-white/55 text-xs mt-0.5">{variant.name}</p>}
              <p className="text-white/70 text-xs mt-1">Qty {quantity}</p>
            </div>
          </div>
        )}

        <div className="border-t border-white/10 pt-4 space-y-2 text-sm">
          <Row label="Subtotal" value={fmt(itemTotal)} testid="sum-subtotal" />
          <Row label="Shipping" value={selectedRate ? fmt(shipping) : "—"} testid="sum-shipping" />
          <div className="border-t border-white/10 pt-3 flex justify-between text-lg font-bold">
            <span>Total</span>
            <span className="text-[#B32027]" data-testid="sum-total">{fmt(grandTotal)}</span>
          </div>
        </div>

        <button
          onClick={onCheckout}
          disabled={submitting || !selectedRate || !shippingComplete || !recipient.email}
          data-testid="merch-pay-button"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white px-6 py-4 text-[11px] font-bold uppercase tracking-[0.3em] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? `Opening secure ${providerLabel} checkout…` : (
            <>Pay {fmt(grandTotal)} · {providerLabel} <ArrowUpRight size={12} weight="bold" /></>
          )}
        </button>

        <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 text-center">
          Payment is secured by {providerLabel}. You&rsquo;ll receive tracking by email once your order ships.
        </p>
      </div>
    </aside>
  );
}
